import _ from 'lodash';
import React from 'react';

import {
  Box,
  IconButton,
  styled
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';

import { paginationUtil } from 'iron-forge-react-modules';



// Disabled is used to prevent further interaction. It also represents the "selected" state.
const PageIconButton = styled(IconButton)(({ theme }) => ({
  fontSize: 14,
  color: 'rgba(72, 108, 130, 0.54)',
  '&:disabled': {
    color: 'rgba(72, 108, 130, 1)'
  }
}));

// When something is not disabled, there are more options.
const ChevronIconButton = styled(IconButton)(({ theme }) => ({
  color: '#E16B50',
  '&:disabled': {
    color: '#C3D1D8'
  }
}));

const PageNumberBox = styled(Box)(({ theme }) => ({
  width: 32,
  height: 32,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}));

const ContainerBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center'
}));



export interface IProps {
  pageInfo: paginationUtil.IPageInfo;

  onChangePage: (pageIndex: number) => void;
}

export const FC: React.FC<IProps> = ({ pageInfo, onChangePage }) => {
  const pageIndices = React.useMemo(() => _.times(pageInfo.pageList.length), [pageInfo]);


  const pagesToRender = React.useMemo(() => {
    const firstPage = _.take(pageIndices, 1);
    const lastPage = _.takeRight(pageIndices, 1);

    const leftChunk = _.takeRight(_.slice(pageIndices, 0, pageInfo.currentPage), 2);
    const rightChunk = _.take(_.slice(pageIndices, pageInfo.currentPage), 3);
    return _.uniq([
      ...firstPage,
      ...leftChunk,
      ...rightChunk,
      ...lastPage
    ]);
  }, [pageIndices, pageInfo.currentPage]);


  const pageButtons = React.useMemo(
    () => _.map(pagesToRender, (pageIndex, arrayIndex) => {
      return (
        <>
          {arrayIndex === 1 && pageIndex !== arrayIndex && (
            <PageIconButton onClick={() => onChangePage(Math.floor((pageIndex - arrayIndex) / 2))}>
              ...
            </PageIconButton>
          )}

          <PageIconButton
            disabled={pageIndex === pageInfo.currentPage}
            onClick={() => onChangePage(pageIndex)}>
            <PageNumberBox>
              {pageIndex + 1}
            </PageNumberBox>
          </PageIconButton>

          {(arrayIndex === pagesToRender.length - 2) && pageIndex !== (pagesToRender[arrayIndex + 1] - 1) && (
            // Go somewhere in the middle of where we currently are and the last page
            <PageIconButton onClick={() => onChangePage(Math.floor((pagesToRender[arrayIndex + 1] - pageIndex) / 2) + pageIndex)}>
              ...
            </PageIconButton>
          )}
        </>
      );
    }),
    [
      onChangePage,
      pagesToRender,
      pageInfo.currentPage
    ]);



  return (
    <ContainerBox>
      <ChevronIconButton
        disabled={pageInfo.currentPage === 0}
        onClick={() => onChangePage(pageInfo.currentPage - 1)}>
        <ChevronLeft />
      </ChevronIconButton>

      {pageButtons}

      <ChevronIconButton
        disabled={pageInfo.currentPage === (pageInfo.pageList.length - 1)}
        onClick={() => onChangePage(pageInfo.currentPage + 1)}>
        <ChevronRight />
      </ChevronIconButton>
    </ContainerBox>
  );
};
